import React, { useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import BasicInfo from '../../_common/BasicInfo/BasicInfo';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../stores/store';
import { fetchConfirmedQc } from '../../stores/confirmedQc/slice';
import { useNavigate } from 'react-router-dom';
import PFCodeTable from './components/PFCodeTable';
import FooterButton from './components/FooterButton';
import { useTranslation } from 'react-i18next';
import { INSPECTION_STATUSES } from '../../_constants/inspectionConst';
import { fetchInspectionTop } from '../../stores/inspectionTop/slice';
import { NavigateReport } from '../../types/NavigateOptionType';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { INSPEX_PATHNAME } from '../../_constants/Const';
import { isFulfilled } from '@reduxjs/toolkit';
import {
  isGetConfirmedQc,
  isGetInspectionTop,
} from '../../_utils/typeGuardUtils';
import { API_CODE } from '../../_constants/apiConst';
import useUpdateStatus from '../../hooks/useUpdateStatus';
import useResetStore from '../../hooks/useResetStore';

const ConfirmedQc: React.FC = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { error, showApiError, showTypeError, hideError } = useErrorModal();
  const { resetInspectionStore } = useResetStore();
  const updateStatus = useUpdateStatus();
  const { confirmedQc } = useSelector((state: RootState) => state.confirmedQc);
  const { repairRequest } = useSelector(
    (state: RootState) => state.repairRequest,
  );
  const { inspectionTop } = useSelector(
    (state: RootState) => state.inspectionTop,
  );

  useEffect(() => {
    if (repairRequest) {
      fetchData(repairRequest.rmaNo);
    }
  }, [repairRequest?.rmaNo]);

  const fetchData = async (rmaNo: string) => {
    // 出荷検査の確定診断情報を取得
    const confirmedQc = await dispatch(fetchConfirmedQc({ rmaNo }));
    if (isFulfilled(confirmedQc)) {
      if (!isGetConfirmedQc(confirmedQc.payload)) {
        showTypeError(API_CODE.CONFIRMED_QC);
        return;
      }
    } else {
      showApiError(confirmedQc.payload, API_CODE.CONFIRMED_QC, () =>
        navigate(INSPEX_PATHNAME.RMA_SEARCH),
      );
      return;
    }
    // 出荷検査Top情報を取得
    const response = await dispatch(
      fetchInspectionTop({ query: { rmaNo }, isInspection: false }),
    );

    if (isFulfilled(response)) {
      if (!isGetInspectionTop(response.payload)) {
        showTypeError(API_CODE.QC_TOP);
      }
    } else {
      showApiError(response.payload, API_CODE.QC_TOP, () =>
        navigate(INSPEX_PATHNAME.RMA_SEARCH),
      );
    }
  };

  const onclickBack = async () => {
    if (!repairRequest) {
      return;
    }
    // ステータスを戻す
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.QC_IN_PROGRESS,
      inspectionTop.qcCount,
      showApiError,
    );
    if (!isSuccess) {
      return;
    }
    resetInspectionStore();
    navigate(INSPEX_PATHNAME.INSPECTION_TOP);
  };

  const onClickComplete = async () => {
    if (!repairRequest) {
      return;
    }
    // ステータスを更新
    const isSuccess = await updateStatus(
      INSPECTION_STATUSES.QC_REPAIRING,
      inspectionTop.qcCount + 1,
      showApiError,
    );
    if (isSuccess) {
      navigate(INSPEX_PATHNAME.REPORT, {
        state: {
          defaultQcCount: inspectionTop.qcCount,
        } as NavigateReport,
      });
    }
  };

  return (
    <Box>
      <BasicInfo />
      <Box sx={{ p: 2, pt: 5 }}>
        {repairRequest?.isCreatedByExFM && (
          <Box sx={{ pb: 2 }}>
            <Typography variant="h6">{t('MSG_MODIFY_DATA_EXFM')}</Typography>
          </Box>
        )}
        {confirmedQc && (
          <React.Fragment>
            <PFCodeTable pfCodes={confirmedQc.pfCodeList} />
            <FooterButton
              onClickBack={onclickBack}
              onClickComplete={onClickComplete}
            />
          </React.Fragment>
        )}
      </Box>
      {error && <ErrorModal message={error} onClose={hideError} />}
    </Box>
  );
};

export default ConfirmedQc;
